'use client'
import { Accordion, Box, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import RichText from '@/app/[locale]/price/_components/Richtext'
import QuestionItem from '@/app/[locale]/price/[slug]/_components/FaqList/QuestionItem'
import { useStockContext } from '@/app/[locale]/price/stocks/[symbol]/_contexts/StockContext'
import { StocksContent } from '@/types/stock'
import { replaceStockPlaceholders } from '@/utils/stockPlaceholderReplacer'

interface FaqSectionProps {
  content: StocksContent
  stockName: string
}

export default function FaqSection({ content, stockName }: FaqSectionProps) {
  const t = useTranslations()
  const { stock } = useStockContext()

  // Create custom converter for text nodes to replace stock placeholders
  const customConverters = useMemo(
    () =>
      ({ defaultConverters }: { defaultConverters: any }) => {
        const originalTextConverter = defaultConverters.text

        return {
          ...defaultConverters,
          text: (args: any) => {
            const { node } = args

            // Replace placeholders in text nodes before default processing
            if (node?.text && typeof node.text === 'string') {
              const replacedText = replaceStockPlaceholders(node.text, stock)

              // Create a new node with replaced text
              const modifiedNode = { ...node, text: replacedText }

              // Call the original converter with modified node
              return originalTextConverter({ ...args, node: modifiedNode })
            }

            // No placeholder replacement needed, use original converter
            return originalTextConverter(args)
          },
        }
      },
    [stock],
  )

  return (
    <>
      <Box
        style={{
          borderRadius: 16,
          maxWidth: '100%',
        }}
      >
        <Text fz={18} fw={600} lh="32px" component="h2" mb={12} c="var(--text-primary)">
          {t('faqTitle', { coinName: stockName })}
        </Text>

        <Accordion
          variant="unstyled"
          styles={{
            content: {
              padding: 0,
            },
            panel: {
              padding: 0,
            },
          }}
        >
          {content.faq!.map((item, index) => (
            <QuestionItem
              key={item.id || `faq-${index}`}
              value={`stock-faq-${index}`}
              question={replaceStockPlaceholders(item.faq.question, stock)}
              answer={<RichText data={item.faq.answer} customConverters={customConverters} />}
              isLast={index === content.faq!.length - 1}
            />
          ))}
        </Accordion>
      </Box>
    </>
  )
}
